'use client'
import { useEffect, useMemo, useState } from 'react'
import type { OrderAddressDTO } from 'ecosystem'
import { useUser } from 'auth'
import { useRememberedAddress } from '../../../hooks'
import {
  companyAddressToOrderAddress,
  pickDefaultShippingAddress,
  resolveRememberedChoice
} from './companyAddressToOrderAddress'

interface UseShippingAddressOptions {
  /**
   * Whether this flow actually owns the delivery address.
   *
   * `false` where a payment widget collects it inside its own iframe — there the
   * shopper never sees this selection, so replaying what they chose on a previous
   * checkout would be invisible state that still fed the shipping-rule country.
   */
  ownsAddress?: boolean
}

/**
 * Tracks the delivery address for a DEFAULT-shipping flow — the SHIPPING mirror of
 * {@link useBillingAddress}, and the single place its precedence is decided.
 *
 * Seeding order: the choice this device remembers, then the company's default SHIPPING
 * address. The remembered choice wins because it is what the shopper actually picked
 * here last time, where the default is only a guess — and because without it a B2B
 * shopper's custom address could never come back.
 *
 * Nothing is chosen on the first render. Reading the company default eagerly would
 * install it before the device has been read, and every seeding branch bails out once
 * something is selected — so the remembered choice would be skipped whenever `useUser`
 * was warm. Worse, the picker would then see the default as the current selection and
 * write it over the shopper's stored choice. Hence the `isReady` gate.
 *
 * This lives in one hook because `DefaultDeliveryCheckout` and `InvoiceDefault` each had
 * their own copy of the chain, and the copies drifted.
 */
export const useShippingAddress = ({ ownsAddress = true }: UseShippingAddressOptions = {}) => {
  const { user } = useUser()
  const { remembered, isReady } = useRememberedAddress('shipping')
  const [shippingAddress, setShippingAddress] = useState<OrderAddressDTO | undefined>(undefined)

  const companyAddresses = user?.customer?.companyProfile?.addresses

  const companyDefault = useMemo<OrderAddressDTO | undefined>(() => {
    const def = pickDefaultShippingAddress(companyAddresses)
    return def ? companyAddressToOrderAddress(def) : undefined
  }, [companyAddresses])

  useEffect(() => {
    if (shippingAddress) return
    // Both branches compete with the device, so wait until it has been read.
    if (!isReady) return

    const chosen = ownsAddress ? resolveRememberedChoice(remembered, companyAddresses) : undefined
    if (chosen) {
      setShippingAddress(chosen)
      return
    }

    if (companyDefault) setShippingAddress(companyDefault)
  }, [companyAddresses, companyDefault, isReady, ownsAddress, remembered, shippingAddress])

  return { shippingAddress, setShippingAddress }
}
